import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../../api/client";
import { toast } from "../../components/ui/toast";
import { apiErrorMessage } from "../../i18n";
import { Alert, AlertDescription } from "../../components/ui/alert";
import { Button } from "../../components/ui/button";
import { Field, FieldDescription, FieldLabel } from "../../components/ui/field";
import { Input } from "../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import type {
  DataSourceDetail,
  SavedDataSource,
  DateSelection,
  ManualColumn,
  ManualSourceConfig,
} from "../../api/types";
import {
  isISO4217CurrencyCode,
  useOrganizationRegionalProfile,
} from "../../settings/regionalProfile";
import { EditorFrame, optionLabel } from "./shared";

const manualColumnTypes: ManualColumn["type"][] = [
  "text",
  "number",
  "integer",
  "percent",
  "currency",
  "boolean",
  "date",
  "datetime",
  "url",
];

const booleanCellOptions = [
  { value: "", labelKey: "dataSources.options.empty" },
  { value: "true", labelKey: "dataSources.options.trueValue" },
  { value: "false", labelKey: "dataSources.options.falseValue" },
] as const;

const manualDateModeOptions = [
  { value: "today", labelKey: "dataSources.options.today" },
  { value: "tomorrow", labelKey: "dataSources.options.tomorrow" },
  { value: "next_available", labelKey: "dataSources.options.nextAvailable" },
  { value: "current_week", labelKey: "dataSources.options.currentWeek" },
] as const;

export function ManualDataSourceEditor({
  dataSource,
  csrf,
  readOnly = false,
  onClose,
  onSaved,
  page,
}: {
  dataSource?: DataSourceDetail;
  csrf: string;
  readOnly?: boolean;
  onClose: () => void;
  onSaved: (dataSource: SavedDataSource) => void;
  page?: boolean;
}) {
  const { t } = useTranslation(["content", "common"]);
  const queryClient = useQueryClient();
  const regional = useOrganizationRegionalProfile();
  const touchedTimezone = useRef(false);
  const originalColumns = useRef(
    (dataSource?.configuration as ManualSourceConfig | undefined)?.columns ??
      [],
  );
  const [name, setName] = useState(dataSource?.name ?? "");
  const [description, setDescription] = useState(dataSource?.description ?? "");
  const [configuration, setConfiguration] = useState<ManualSourceConfig>(
    (dataSource?.configuration as ManualSourceConfig | undefined) ?? {
      // i18n-ignore: default author-owned column, stored in the configuration
      columns: [{ key: "title", label: "Title", type: "text" }],
      rows: [{ id: crypto.randomUUID(), values: { title: "" } }],
      dateSelection: {
        enabled: false,
        dateFormat: "auto",
        timezone: "UTC",
        mode: "today",
        excludePast: false,
        noMatchBehavior: "empty",
      },
    },
  );
  useEffect(() => {
    if (dataSource || !regional.ready || touchedTimezone.current) return;
    setConfiguration((current) => ({
      ...current,
      dateSelection: {
        ...current.dateSelection,
        timezone: regional.timezone,
      },
    }));
  }, [dataSource, regional.ready, regional.timezone]);
  const save = useMutation({
    mutationFn: () => {
      const input = {
        provider: "manual" as const,
        name,
        description,
        configuration,
      };
      return dataSource
        ? api.updateDataSource(dataSource.id, input, csrf)
        : api.createDataSource(input, csrf);
    },
    onSuccess: (saved) => {
      toast.add({
        title: dataSource ? "Data Source updated." : "Data Source created.",
        type: "success",
      });
      void queryClient.invalidateQueries({ queryKey: ["data-sources"] });
      onSaved(saved);
    },
  });
  const updateColumn = (index: number, patch: Partial<ManualColumn>) =>
    setConfiguration((current) => ({
      ...current,
      columns: current.columns.map((column, columnIndex) =>
        columnIndex === index ? { ...column, ...patch } : column,
      ),
    }));
  const isUnspecifiedLegacyCurrency = (column: ManualColumn) =>
    originalColumns.current.some(
      (original) =>
        original.key === column.key &&
        original.type === "currency" &&
        !original.currency,
    );
  // Column type labels are translated at render; the stored values stay API tokens.
  const columnTypeLabels: Record<ManualColumn["type"], string> = {
    text: t("dataSources.columnTypes.text"),
    number: t("dataSources.columnTypes.number"),
    integer: t("dataSources.columnTypes.integer"),
    percent: t("dataSources.columnTypes.percent"),
    currency: t("dataSources.columnTypes.currency"),
    boolean: t("dataSources.columnTypes.boolean"),
    date: t("dataSources.columnTypes.date"),
    datetime: t("dataSources.columnTypes.datetime"),
    url: t("dataSources.columnTypes.url"),
  };
  return (
    <EditorFrame
      title={
        dataSource
          ? t("dataSources.manual.titleEdit")
          : t("dataSources.manual.titleCreate")
      }
      description={t("dataSources.manual.description")}
      page={page}
      onClose={onClose}
      footer={
        !readOnly && (
          <Button
            type="button"
            disabled={
              save.isPending ||
              !name.trim() ||
              configuration.columns.some(
                (column) =>
                  column.type === "currency" &&
                  !isISO4217CurrencyCode(column.currency ?? "") &&
                  !isUnspecifiedLegacyCurrency(column),
              )
            }
            onClick={() => save.mutate()}
          >
            {save.isPending
              ? t("common:actions.saving")
              : t("dataSources.editor.save")}
          </Button>
        )
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field>
          <FieldLabel htmlFor="manual-name">
            {t("dataSources.editor.name")}
          </FieldLabel>
          <Input
            id="manual-name"
            value={name}
            disabled={readOnly}
            onChange={(event) => setName(event.target.value)}
          />
        </Field>
        <Field>
          <FieldLabel htmlFor="manual-description">
            {t("dataSources.editor.description")}
          </FieldLabel>
          <Input
            id="manual-description"
            value={description}
            disabled={readOnly}
            onChange={(event) => setDescription(event.target.value)}
          />
        </Field>
      </div>
      <fieldset className="grid gap-3">
        <legend className="text-sm font-medium">
          {t("dataSources.manual.columnsLegend")}
        </legend>
        {configuration.columns.map((column, index) => (
          <div
            className="grid gap-4 sm:grid-cols-3"
            key={`${column.key}-${index}`}
          >
            <Field>
              <FieldLabel htmlFor={`manual-column-key-${index}`}>
                {t("dataSources.manual.columnKey")}
              </FieldLabel>
              <Input
                id={`manual-column-key-${index}`}
                value={column.key}
                disabled={readOnly}
                onChange={(event) =>
                  updateColumn(index, { key: event.target.value })
                }
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={`manual-column-label-${index}`}>
                {t("dataSources.manual.columnLabel")}
              </FieldLabel>
              <Input
                id={`manual-column-label-${index}`}
                value={column.label}
                disabled={readOnly}
                onChange={(event) =>
                  updateColumn(index, { label: event.target.value })
                }
              />
            </Field>
            <Field>
              <FieldLabel htmlFor={`manual-column-type-${index}`}>
                {t("dataSources.manual.columnType")}
              </FieldLabel>
              <Select
                items={manualColumnTypes.map((type) => ({
                  value: type,
                  label: columnTypeLabels[type],
                }))}
                value={column.type}
                disabled={readOnly}
                onValueChange={(next) =>
                  updateColumn(index, {
                    type: next as ManualColumn["type"],
                  })
                }
              >
                <SelectTrigger
                  id={`manual-column-type-${index}`}
                  aria-label={t("dataSources.manual.typeForColumn", {
                    label:
                      column.label ||
                      t("dataSources.manual.columnFallback", {
                        index: index + 1,
                      }),
                  })}
                >
                  <SelectValue>{columnTypeLabels[column.type]}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {manualColumnTypes.map((type) => (
                    <SelectItem key={type} value={type}>
                      {columnTypeLabels[type]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {column.type === "currency" && (
              <Field>
                <FieldLabel htmlFor={`manual-column-currency-${index}`}>
                  {t("dataSources.manual.currency")}
                </FieldLabel>
                <Input
                  id={`manual-column-currency-${index}`}
                  value={column.currency ?? ""}
                  required={!isUnspecifiedLegacyCurrency(column)}
                  maxLength={3}
                  disabled={readOnly}
                  onChange={(event) =>
                    updateColumn(index, {
                      currency: event.target.value.toUpperCase(),
                    })
                  }
                />
                <FieldDescription>
                  {t("dataSources.manual.currencyHint")}
                </FieldDescription>
              </Field>
            )}
            {!readOnly && configuration.columns.length > 1 && (
              <Button
                type="button"
                variant="destructive"
                onClick={() =>
                  setConfiguration((current) => ({
                    ...current,
                    columns: current.columns.filter((_, i) => i !== index),
                    rows: current.rows.map((row) => {
                      const values = { ...row.values };
                      delete values[column.key];
                      return { ...row, values };
                    }),
                  }))
                }
              >
                <Trash2 size={15} aria-hidden="true" />{" "}
                {t("dataSources.manual.removeColumn")}
              </Button>
            )}
          </div>
        ))}
        {!readOnly && configuration.columns.length < 12 && (
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              setConfiguration((current) => ({
                ...current,
                columns: [
                  ...current.columns,
                  {
                    key: `field_${current.columns.length + 1}`,
                    // i18n-ignore: generated default label the author renames
                    label: `Field ${current.columns.length + 1}`,
                    type: "text",
                  },
                ],
              }))
            }
          >
            <Plus size={15} aria-hidden="true" />{" "}
            {t("dataSources.manual.addColumn")}
          </Button>
        )}
      </fieldset>
      <fieldset className="grid gap-3">
        <legend className="text-sm font-medium">
          {t("dataSources.manual.rowsLegend", {
            rows: configuration.rows.length,
          })}
        </legend>
        <div className="grid gap-3">
          {configuration.rows.map((row, rowIndex) => (
            <div
              className="grid gap-4 rounded-lg border p-3 sm:grid-cols-2"
              key={row.id}
            >
              {configuration.columns.map((column) => (
                <Field key={column.key}>
                  <FieldLabel htmlFor={`manual-cell-${rowIndex}-${column.key}`}>
                    {column.label}
                  </FieldLabel>
                  {column.type === "boolean" ? (
                    <Select
                      value={row.values[column.key] ?? ""}
                      disabled={readOnly}
                      onValueChange={(next) =>
                        setConfiguration((current) => ({
                          ...current,
                          rows: current.rows.map((item, index) =>
                            index === rowIndex
                              ? {
                                  ...item,
                                  values: {
                                    ...item.values,
                                    [column.key]: next as string,
                                  },
                                }
                              : item,
                          ),
                        }))
                      }
                      items={booleanCellOptions.map((option) => ({
                        value: option.value,
                        label: t(option.labelKey),
                      }))}
                    >
                      <SelectTrigger
                        id={`manual-cell-${rowIndex}-${column.key}`}
                        aria-label={t("dataSources.manual.cellValue", {
                          label: column.label,
                        })}
                      >
                        <SelectValue>
                          {optionLabel(
                            booleanCellOptions.map((option) => ({
                              value: option.value,
                              label: t(option.labelKey),
                            })),
                            row.values[column.key] ?? "",
                          )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {booleanCellOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {t(option.labelKey)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input
                      id={`manual-cell-${rowIndex}-${column.key}`}
                      type={
                        column.type === "date"
                          ? "date"
                          : column.type === "datetime"
                            ? "text"
                            : [
                                  "number",
                                  "integer",
                                  "percent",
                                  "currency",
                                ].includes(column.type)
                              ? "number"
                              : "text"
                      }
                      value={row.values[column.key] ?? ""}
                      disabled={readOnly}
                      onChange={(event) =>
                        setConfiguration((current) => ({
                          ...current,
                          rows: current.rows.map((item, index) =>
                            index === rowIndex
                              ? {
                                  ...item,
                                  values: {
                                    ...item.values,
                                    [column.key]: event.target.value,
                                  },
                                }
                              : item,
                          ),
                        }))
                      }
                    />
                  )}
                </Field>
              ))}
              {!readOnly && (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t("dataSources.manual.removeRow", {
                    index: rowIndex + 1,
                  })}
                  onClick={() =>
                    setConfiguration((current) => ({
                      ...current,
                      rows: current.rows.filter(
                        (_, index) => index !== rowIndex,
                      ),
                    }))
                  }
                >
                  <Trash2 size={15} aria-hidden="true" />
                </Button>
              )}
            </div>
          ))}
        </div>
        {!readOnly && configuration.rows.length < 200 && (
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              setConfiguration((current) => ({
                ...current,
                rows: [
                  ...current.rows,
                  { id: crypto.randomUUID(), values: {} },
                ],
              }))
            }
          >
            <Plus size={15} aria-hidden="true" />{" "}
            {t("dataSources.manual.addRow")}
          </Button>
        )}
      </fieldset>
      <fieldset className="grid gap-3">
        <legend className="text-sm font-medium">
          {t("dataSources.manual.dateAware")}
        </legend>
        {/* The wrapping label names the switch; no extra aria-label. */}
        <label className="flex items-center gap-2 text-sm">
          <Switch
            checked={configuration.dateSelection.enabled}
            disabled={readOnly}
            onCheckedChange={(checked) =>
              setConfiguration((current) => ({
                ...current,
                dateSelection: {
                  ...current.dateSelection,
                  enabled: checked === true,
                },
              }))
            }
          />
          <span>{t("dataSources.manual.selectByLocalDate")}</span>
        </label>
        {configuration.dateSelection.enabled && (
          <div className="grid gap-4 sm:grid-cols-3">
            <Field>
              <FieldLabel htmlFor="manual-date-field">
                {t("dataSources.manual.dateField")}
              </FieldLabel>
              <Select
                value={configuration.dateField ?? ""}
                disabled={readOnly}
                onValueChange={(next) =>
                  setConfiguration((current) => ({
                    ...current,
                    dateField: next as string,
                  }))
                }
                items={[
                  {
                    value: "",
                    label: t("dataSources.manual.selectDateColumn"),
                  },
                  ...configuration.columns
                    .filter((column) =>
                      ["date", "datetime"].includes(column.type),
                    )
                    .map((column) => ({
                      value: column.key,
                      label: column.label,
                    })),
                ]}
              >
                <SelectTrigger
                  id="manual-date-field"
                  aria-label={t("dataSources.manual.dateField")}
                >
                  <SelectValue>
                    {optionLabel(
                      [
                        {
                          value: "",
                          label: t("dataSources.manual.selectDateColumn"),
                        },
                        ...configuration.columns
                          .filter((column) =>
                            ["date", "datetime"].includes(column.type),
                          )
                          .map((column) => ({
                            value: column.key,
                            label: column.label,
                          })),
                      ],
                      configuration.dateField ?? "",
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="">
                    {t("dataSources.manual.selectDateColumn")}
                  </SelectItem>
                  {configuration.columns
                    .filter((column) =>
                      ["date", "datetime"].includes(column.type),
                    )
                    .map((column) => (
                      <SelectItem key={column.key} value={column.key}>
                        {column.label}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="manual-timezone">
                {t("dataSources.editor.timezone")}
              </FieldLabel>
              <Input
                id="manual-timezone"
                value={configuration.dateSelection.timezone}
                disabled={readOnly}
                onChange={(event) => {
                  touchedTimezone.current = true;
                  setConfiguration((current) => ({
                    ...current,
                    dateSelection: {
                      ...current.dateSelection,
                      timezone: event.target.value,
                    },
                  }));
                }}
              />
            </Field>
            <Field>
              <FieldLabel htmlFor="manual-selection">
                {t("dataSources.manual.selection")}
              </FieldLabel>
              <Select
                value={configuration.dateSelection.mode}
                disabled={readOnly}
                onValueChange={(next) =>
                  setConfiguration((current) => ({
                    ...current,
                    dateSelection: {
                      ...current.dateSelection,
                      mode: next as DateSelection["mode"],
                    },
                  }))
                }
                items={manualDateModeOptions.map((option) => ({
                  value: option.value,
                  label: t(option.labelKey),
                }))}
              >
                <SelectTrigger
                  id="manual-selection"
                  aria-label={t("dataSources.manual.selection")}
                >
                  <SelectValue>
                    {optionLabel(
                      manualDateModeOptions.map((option) => ({
                        value: option.value,
                        label: t(option.labelKey),
                      })),
                      configuration.dateSelection.mode,
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {manualDateModeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>
        )}
      </fieldset>
      {save.error && (
        <Alert variant="destructive">
          <AlertDescription>{apiErrorMessage(save.error)}</AlertDescription>
        </Alert>
      )}
    </EditorFrame>
  );
}
